import { SnackbarEvents } from "./../../shared/components/ui/snackbar/snackbar.events";
import { IPromise, IScope, ITimeoutService } from "angular";
import { HierarchyNode } from "d3";
import { ITeam, ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { CurrentUserService } from "@gtmhub/users/currentUserService";
import { IStateInit } from "@gtmhub/core/routing";
import { ITeamDeletedArgs, ITeamEventArgs, TeamEvents } from "../events";
import { AccountResolverService } from "@gtmhub/state/accountResolverService";
import { AccountType } from "@gtmhub/core";
import { indexOfSameClassSiblings } from "@gtmhub/shared/dom";
import { IdMap } from "@gtmhub/util";
import { INgRedux } from "@gtmhub/state-management";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { createTeamsTree } from "@gtmhub/shared/tree-renderer/utils";

export interface IOrganizationScope extends IScope, IExtendedRootScopeService, IOrganizationRedux {
  loading: {
    tree: boolean;
    treeUpdates: boolean;
  };
  tree: HierarchyNode<ITeam>;
  mode: string;
  isDemoAccount: boolean;
  drawLines(): void;
  collapseAll(): void;
  expandAll(): void;
  toggle(team: ITeam): void;
  changeMode(mode: string): void;
}

interface IOrganizationRedux {
  teamsIdMap: IdMap<ITeam>;
  teams: ITeam[];
  teamsFetched: boolean;
  teamsError: IUIError;
}

const bindStateToScope = (state: ITeamsStoreState): IOrganizationRedux => {
  return {
    teamsIdMap: state.teams.map,
    teams: state.teams.items,
    teamsFetched: state.teams.isFetched,
    teamsError: state.teams.error,
  };
};

export class OrganizationCtrl implements IStateInit {
  private teamEvents: TeamEvents;
  private snackbarEvents: SnackbarEvents;

  public static $inject = ["$scope", "$ngRedux", "TeamsActions", "CurrentUserService", "$timeout", "AccountResolverService", "UIErrorHandlingService"];

  constructor(
    private $scope: IOrganizationScope,
    private $ngRedux: INgRedux,
    private teamsActions: TeamsActions,
    private currentUserService: CurrentUserService,
    private $timeout: ITimeoutService,
    private accountResolverService: AccountResolverService,
    private uiErrorHandlingService: UIErrorHandlingService
  ) {
    this.$scope.loading = {
      tree: true,
      treeUpdates: false,
    };

    this.teamEvents = new TeamEvents($scope);
    this.snackbarEvents = new SnackbarEvents($scope);

    this.$scope.isDemoAccount = this.accountResolverService.getAccountData().type === AccountType.DemoAccount;

    this.$scope.drawLines = this.drawLines;
    this.$scope.toggle = this.toggle;
    this.$scope.collapseAll = this.collapseAll;
    this.$scope.expandAll = this.expandAll;
    this.$scope.changeMode = this.changeMode;

    this.$scope.mode = this.currentUserService.getUserSetting("teamsView") || "medium";
    this.teamEvents.onTeamDeleted((args) => this.onTeamDeleted(args));
    this.teamEvents.onTeamCreated((args) => this.onTeamCreated(args));
    this.teamEvents.onTeamUpdated(() => this.onTeamsChanged());

    const unsubscribe = $ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    $ngRedux.dispatch(teamsActions.fetchTeamsIfMissing());
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private onTeamsChanged = () => {
    this.$scope.loading.treeUpdates = true;
    this.$scope.teamsFetched = false;
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.init();
  };

  private onTeamCreated = (args: ITeamEventArgs): void => {
    this.snackbarEvents.broadcastShowSnackbar("create_team_success", { customDuration: 4000, inputPlaceholders: { teamName: args.team.name } });
    if (this.$scope.teams.filter((team) => team.id === args.team.id).length === 0) {
      this.$scope.teams.push(args.team);
    }
    this.$scope.loading.treeUpdates = true;
    this.init();
  };

  private onTeamDeleted = (args: ITeamDeletedArgs): void => {
    this.$scope.teams = this.$scope.teams.reduce((teamArr, t) => {
      if (t.id === args.teamId) {
        return teamArr;
      }
      if (t.parentId === args.teamId) {
        t.parentId = args.parentId || "root";
      }
      teamArr.push(t);
      return teamArr;
    }, []);
    this.$scope.tree = createTeamsTree(this.$scope.teams, this.$scope.teamsIdMap) as HierarchyNode<ITeam>;
    this.drawLines();
  };

  private collapseAll = () => {
    for (const team of this.$scope.teams) {
      team.folding = true;
      team.folded = true;
    }
  };

  private expandAll = () => {
    for (const team of this.$scope.teams) {
      team.folding = false;
      team.folded = false;
    }

    this.$timeout(() => {
      this.drawLines();
    }, 50);
  };

  private changeMode = (mode: string) => {
    this.$scope.mode = mode;
    this.currentUserService.setUserSetting({ teamsView: mode });

    document.querySelectorAll(".team-item").forEach((el) => el.classList.add("faded"));

    this.$timeout(() => {
      this.drawLines();
    }, 200);
  };

  private toggle = (team: ITeam) => {
    document.querySelectorAll(".team-item").forEach((el) => el.classList.add("faded"));

    if (team.folded === true) {
      team.folded = false;
      this.$timeout(() => {
        team.folding = false;
      }, 1);
      this.$timeout(() => {
        this.drawLines();
      }, 200);
    } else {
      team.folding = true;
      this.$timeout(() => {
        team.folded = true;
        this.drawLines();
      }, 200);
    }
  };

  private init = () => {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.teamsFetched,
        () => this.$scope.teamsError
      )
      .then(
        () => {
          this.$scope.teams = this.$scope.teams.sort((a, b) => (a.name === b.name ? 0 : a.name < b.name ? -1 : 1));
          this.$scope.tree = createTeamsTree(this.$scope.teams, this.$scope.teamsIdMap) as HierarchyNode<ITeam>;
          this.$scope.loading.tree = false;
          this.$scope.loading.treeUpdates = false;
          this.drawLines();
        },
        (error) => {
          this.$scope.loading.tree = false;
          this.uiErrorHandlingService.handleModal(error);
        }
      );
  };

  private drawLines = () => {
    document.querySelectorAll(".team-item").forEach((el) => el.classList.add("faded"));

    let multiplier = 1;
    if (this.$scope.mode === "relaxed") {
      multiplier = 2;
    }

    this.$timeout(() => {
      document.querySelectorAll<HTMLElement>(".team-item").forEach((element, collectionIndex) => {
        const isRoot = collectionIndex === 0;
        if (isRoot) {
          return;
        }

        const svgEl = element.parentElement.querySelector(".svg");
        svgEl.classList.add("visible");

        const top = element.offsetTop + 20;
        const localIndex = indexOfSameClassSiblings(element, "team-item");

        svgEl
          .querySelector(`svg g:nth-child(${localIndex + 1}) path`)
          .setAttribute("d", "M20,20  C" + 65 * multiplier + ",20  " + 80 * multiplier + "," + top + " " + 100 * multiplier + "," + top);
      });
    }, 50);

    this.$timeout(() => {
      document.querySelectorAll(".team-item").forEach((el) => el.classList.remove("faded"));
    }, 100);
  };
}
